import { g } from "analytics";
import { apparatusClass } from "apparatus/library";
import { apparatusCategories } from "apparatus/specs";
import * as React from "react";
import { Trans, useTranslation } from "react-i18next";
import { Bcn } from "support/beacon";
import { compileSearchTerms, createMiniSearch, NO_SEARCH_TERM_SENTINEL, NoSearchResultsReporter, searchApparatusUsingMiniSearch } from "../../apparatus/search";
import apparatusSpecList from "../../apparatus/spec_list";
import { ApparatusItem } from "./apparatus_item";
const noSearchResultsReporter = new NoSearchResultsReporter();
/**
 * Component that displays a list of apparatus. Clicking on an item adds it.
 * This is also used for a filtered apparatus search.
 */
export const ApparatusList = ({ searchTerm, module, onSwitchModule }) => {
    const { t, i18n } = useTranslation();
    const compiledApparatusSpecs = React.useMemo(() => {
        return apparatusSpecList
            // Filter out apparatus that is not yet released.
            .filter(spec => { var _a; return ((_a = spec.released) !== null && _a !== void 0 ? _a : true); })
            .map(spec => {
            const properties = apparatusClass(spec.type).properties;
            // Translate apparatus name.
            const name = t(`apparatus.${spec.type}.name`, properties.label);
            // Translate search terms. Fallback to a non-existent key that then gets removed
            const searchTermsString = t(`apparatus.${spec.type}.search`, NO_SEARCH_TERM_SENTINEL).toLowerCase();
            return Object.assign(Object.assign(Object.assign({}, spec), { name, lowerCaseName: name.toLowerCase() }), compileSearchTerms(searchTermsString, name));
        });
    }, [t]);
    const minisearch = React.useMemo(() => createMiniSearch(compiledApparatusSpecs, i18n.resolvedLanguage), [compiledApparatusSpecs, i18n.resolvedLanguage]);
    const apparatusByGroups = React.useMemo(() => {
        return {
            "chemistry": splitApparatusByCategory(compiledApparatusSpecs, "chemistry"),
            "heating": splitApparatusByCategory(compiledApparatusSpecs, "heating"),
            "various": splitApparatusByCategory(compiledApparatusSpecs, "various"),
            "shapes": splitApparatusByCategory(compiledApparatusSpecs, "shapes"),
            "biology": splitApparatusByCategory(compiledApparatusSpecs, "biology"),
            "physics": splitApparatusByCategory(compiledApparatusSpecs, "physics"),
            "distillation": splitApparatusByCategory(compiledApparatusSpecs, "distillation"),
            "measurement": splitApparatusByCategory(compiledApparatusSpecs, "measurement"),
            "transport": splitApparatusByCategory(compiledApparatusSpecs, "transport")
        };
    }, [compiledApparatusSpecs]);
    const searchResults = React.useMemo(() => {
        const results = searchApparatusUsingMiniSearch(minisearch, searchTerm);
        if (searchTerm && searchTerm.length > 0 && results.length === 0) {
            noSearchResultsReporter.report(searchTerm);
        }
        return results;
    }, [searchTerm, minisearch]);
    const isInSearchMode = searchTerm.length > 0;
    return React.createElement("div", { className: "sidebar-list" },
        React.createElement("div", { className: "scrollable-container", "data-simplebar": true },
            React.createElement("div", null, isInSearchMode
                ? React.createElement(SearchResults, { key: "searchResults", searchTerm: searchTerm, results: searchResults, onSwitchModule: onSwitchModule })
                : React.createElement(DefaultList, { key: "searchResults", groups: apparatusByGroups[module] }))));
};
const SearchResults = ({ results, onSwitchModule, searchTerm }) => {
    const { t, i18n } = useTranslation();
    const moleculePartString = t("search.molecule_part_string");
    return React.createElement(React.Fragment, null,
        React.createElement("div", { key: "searchResultSummary", className: "search-results-summary" }, t("ui.result", { count: results.length })),
        results.map(a => React.createElement(ApparatusItem, Object.assign({ showCategory: true, isSearchMode: true, key: a.type }, a))),
        (searchTerm.toLowerCase() === "probeta" && i18n.language !== "es") && React.createElement(SwitchToSpanish, null),
        (results.length > 0 && searchTerm.length > 2)
            && React.createElement(ReportBadSearchResults, { searchTerm: searchTerm }),
        results.length == 0
            ? (searchTerm.toLowerCase().indexOf(moleculePartString) > -1
                ? React.createElement(MolViewTip, null)
                : React.createElement(NoResultsTip, { onSwitchToShapes: () => onSwitchModule("shapes") }))
            : undefined);
};
/** Remembers the collapse state of groups. True = expanded. */
const groupCollapseMemo = {};
const DefaultList = (props) => {
    const { t } = useTranslation();
    function onHeaderClick(id, el) {
        var _a, _b;
        const collapsed = el.classList.contains("collapsed");
        groupCollapseMemo[id] = !collapsed;
        // If collapsing and the header is sticky,
        // scroll to the header element to prevent weird artifacts.
        if (collapsed) {
            const scrollable = document.getElementsByClassName("scrollable-container")[0];
            const sb = SimpleBar.instances.get(scrollable);
            const groupOffsetStart = (_b = (_a = el.parentElement) === null || _a === void 0 ? void 0 : _a.offsetTop) !== null && _b !== void 0 ? _b : 0;
            if (sb.getScrollElement().scrollTop > groupOffsetStart) {
                $(sb.getScrollElement()).animate({ scrollTop: groupOffsetStart }, 100);
            }
        }
    }
    return React.createElement(React.Fragment, null,
        props.groups.map(group => {
            var _a;
            const id = `apparatus-group-${group.category.replace(/[^\w]/g, "_")}`;
            const show = (_a = groupCollapseMemo[id]) !== null && _a !== void 0 ? _a : true;
            return [
                React.createElement("div", { className: "category-group-wrapper", key: group.category },
                    React.createElement("h3", { "data-toggle": "collapse", "aria-expanded": show ? "true" : "false", "data-target": "#" + id, onClick: (e) => onHeaderClick(id, e.nativeEvent.target) },
                        React.createElement("i", { className: "fas fa-caret-right fa-fw" }),
                        t("category." + group.category)),
                    React.createElement("div", { className: `category-group collapse ${"show".if(show)}`, key: `group_${group.category}`, id: id }, group.apparatus.map(a => React.createElement(ApparatusItem, Object.assign({ isSearchMode: false, key: a.type }, a)))))
            ];
        }),
        props.extraView);
};
function splitApparatusByCategory(allApparatus, module) {
    return apparatusCategories[module].map(category => ({
        category: category,
        apparatus: allApparatus.filter(spec => spec.category == category)
    }));
}
const NoResultsTip = (props) => {
    const { t } = useTranslation();
    return React.createElement("div", { className: "text-secondary p-3" },
        React.createElement("i", { className: "fad fa-lightbulb-on text-warning fa-3x d-block my-3 text-center" }),
        React.createElement(Trans, { i18nKey: "ui.no_results_tip", values: { module: t("apparatus_module.shapes") } },
            "Didn't find an item you need? Try using ",
            React.createElement("span", { className: "href font-weight-bold", onClick: () => props.onSwitchToShapes() }, "Shapes"),
            " to draw it yourself or ",
            React.createElement("span", { className: "href font-weight-bold", onClick: () => Bcn.openFeatureSuggestion() }, "send us a suggestion.")));
};
const ReportBadSearchResults = ({ searchTerm }) => {
    const { t } = useTranslation();
    const [reported, setReported] = React.useState(false);
    React.useEffect(() => {
        setReported(false);
    }, [searchTerm]);
    const report = React.useCallback((term) => {
        g("Bad search results", t("currentLanguageShort") + ":" + term);
        setReported(true);
    }, []);
    return React.createElement("div", { className: "text-secondary p-3" },
        React.createElement("div", null, t("ui.bad_search_results.prompt", "Bad search results?")),
        !reported
            ? React.createElement("div", { className: "href-muted", onClick: () => report(searchTerm) },
                React.createElement("i", { className: "fas fa-flag-swallowtail mr-2" }),
                t("ui.bad_search_results.report", "Let us know"))
            : React.createElement("div", { className: "font-weight-bold" },
                React.createElement("i", { className: "fas fa-check mr-2" }),
                t("ui.bad_search_results.thanks", "Thanks!")));
};
export const PhysicsNotice = () => (React.createElement("div", { className: "p-3 text-white" },
    React.createElement("i", { className: "fad fa-poll-people fa-3x d-block my-3 text-center" }),
    React.createElement("p", null,
        "Physics items are coming this ",
        React.createElement("b", null, "May"),
        ". Vote for items you'd like to see first."),
    React.createElement("a", { href: "https://docs.google.com/forms/d/e/1FAIpQLSd8qDn_Ursbhz_eHewpQxgKSBsi3Pk9GTcf3gAQWy5knw-bUA/viewform", target: "_blank", className: "ui-button" }, "Vote")));
export const SwitchToSpanish = () => {
    const { i18n } = useTranslation();
    function changeLanguage() {
        g("ChangeLanguage:Sidebar", "es");
        i18n.changeLanguage("es");
    }
    return React.createElement("div", { className: "p-2 mt-2" },
        React.createElement("div", { onClick: changeLanguage, className: "ui-button narrower" },
            React.createElement("i", { className: "far fa-globe mr-hem" }),
            "Cambiar al Espa\u00F1ol"));
};
export const PhysicsNoticeMoreComingSoon = () => (React.createElement("div", { className: "m-3 pt-2 text-white border-top border-secondary" },
    React.createElement("i", { className: "fal fa-space-station-moon-alt fa-3x d-block my-3 text-center text-muted" }),
    React.createElement("p", null, "Physics module is in active development. More items coming soon.")));
export const MolViewTip = () => (React.createElement("div", { className: "text-secondary p-3" },
    React.createElement("img", { src: "/images/molview.png", alt: "MolView", height: "48", width: "48", className: "d-block my-3 mx-auto" }),
    React.createElement(Trans, { i18nKey: "ui.molview_search_tip" },
        "Looking to draw molecules? Try ",
        React.createElement("a", { href: "https://molview.com/", className: "font-weight-bold", target: "_blank" }, "MolView"))));
